import { useTranslation } from 'react-i18next'
import { Badge, Card, CardBody, CardDescription, CardHeader, CardTitle } from '@ludus/ui'
import { useDraftBackfill } from '../../hooks/index.ts'
import type { AuditCoverage } from '../../types.ts'
import { formatDate, formatPeriod } from '../../utils/format.ts'

/**
 * An uncovered period leaves the audit a draft (§7.2.1): the page shows each
 * pipeline's import instead of failing, and Run stays disabled until the
 * mirror covers the period.
 */
export function DraftBackfillPanel({ missing }: { missing: AuditCoverage['missing'] }) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('pages.audits.draft.title', { defaultValue: 'Waiting for the backfill' })}
        </CardTitle>
        <CardDescription>
          {t('pages.audits.draft.description', {
            defaultValue:
              'The mirror does not cover the period yet. Each pipeline imports it; the snapshot follows on its own, and the stages stay disabled until then.',
          })}
        </CardDescription>
      </CardHeader>
      <CardBody>
        <ul className="flex flex-col gap-2">
          {missing.map((gap) => (
            <BackfillRow
              key={gap.pipelineId}
              pipelineId={gap.pipelineId}
              from={gap.from}
              to={gap.to}
            />
          ))}
        </ul>
      </CardBody>
    </Card>
  )
}

function BackfillRow({ pipelineId, from, to }: { pipelineId: string; from: Date; to: Date }) {
  const { t } = useTranslation()
  const progress = useDraftBackfill(pipelineId)
  const job = progress?.job ?? null
  return (
    <li className="flex flex-wrap items-center gap-3 text-sm">
      <span className="font-medium text-foreground">{pipelineId}</span>
      <span className="text-xs text-muted">{formatPeriod(from, to)}</span>
      {job === null ? (
        <Badge tone="muted" size="sm">
          {t('pages.audits.draft.notStarted', { defaultValue: 'not started' })}
        </Badge>
      ) : (
        <Badge
          tone={job.state === 'failed' ? 'danger' : job.state === 'done' ? 'success' : 'info'}
          size="sm"
        >
          {t(`pages.audits.draft.job.${job.state}`, { defaultValue: job.state })}
        </Badge>
      )}
      {job && job.pages > 0 && (
        <span className="text-xs text-muted">
          {t('pages.audits.draft.pages', { defaultValue: '{{n}} pages', n: job.pages })}
        </span>
      )}
      {job?.error && <span className="text-xs text-toast-error-text">{job.error}</span>}
      {progress?.backfilledFrom && progress.backfilledTo && (
        <span className="text-xs text-muted">
          {t('pages.audits.draft.covered', {
            defaultValue: 'covered {{from}} → {{to}}',
            from: formatDate(progress.backfilledFrom),
            to: formatDate(progress.backfilledTo),
          })}
        </span>
      )}
    </li>
  )
}
